/**
 * Display step based on current step number
 * This is a simplified version to improve readability in the main component
 */
import React from 'react';
import { 
  LoanTypeStep,
  // Employed Individual Path
  EmployedPersonalStep,
  EmployedLoanDetailsStep,
  EmployedAdditionalStep,
  // Business Owner Path
  BusinessProfileStep,
  BusinessLoanStep,
  BusinessAdditionalStep
} from './FormSteps';

export const renderFormStep = (currentStep, formData, handleInputChange, errors, nextStep, prevStep, isSubmitting) => {
  // Common button components
  const PreviousButton = () => (
    <button type="button" className="secondary-button" onClick={prevStep}>
      <span className="button-text">Previous</span>
    </button>
  );

  const NextButton = () => (
    <button type="button" className="primary-button next-button" onClick={nextStep}>
      <span className="button-text">Next</span>
    </button>
  );

  const SubmitButton = () => (
    <button 
      type="submit" 
      className="primary-button"
      disabled={isSubmitting}
    >
      <span className="button-text">
        {isSubmitting ? "Submitting..." : "Submit"}
      </span>
    </button>
  );

  // Common props for all steps
  const stepProps = {
    formData,
    handleInputChange,
    errors
  };

  // Step 1 is always the loan type selection, but now combined with Step 2 content
  if (currentStep === 1) {
    return (
      <div className="form-step combined-step">
        <LoanTypeStep {...stepProps} />
        
        {/* Show step 2 content immediately after selection */}
        {formData.applicantType === "Employed Individual" && (
          <div className="conditional-step-content">
            <h3 className="step-subtitle">Please complete your personal details</h3>
            <EmployedPersonalStep {...stepProps} />
          </div>
        )}
        
        {formData.applicantType === "Business Owner" && (
          <div className="conditional-step-content">
            <h3 className="step-subtitle">Please complete your business profile</h3>
            <BusinessProfileStep {...stepProps} />
          </div>
        )}
        
        <div className="form-buttons">
          {formData.applicantType ? (
            <NextButton />
          ) : (
            <button type="button" className="primary-button next-button" disabled={true}>
              <span className="button-text">Select an option above to continue</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Handle the Employed Individual path (steps 2-3 after combining)
  if (formData.applicantType === "Employed Individual") {
    switch(currentStep) {
      // Step 2 is now Loan Details (was previously step 3)
      case 2:
        return (
          <div className="form-step">
            <EmployedLoanDetailsStep {...stepProps} />
            <div className="form-buttons">
              <PreviousButton />
              <NextButton />
            </div>
          </div>
        );
        
      // Step 3 is now Additional Info (was previously step 4)
      case 3:
        return (
          <div className="form-step">
            <EmployedAdditionalStep {...stepProps} />
            <div className="form-buttons">
              <PreviousButton />
              <SubmitButton />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  }
  
  // Handle the Business Owner path (steps 2-3 after combining)
  if (formData.applicantType === "Business Owner") {
    switch(currentStep) {
      // Step 2 is now Loan Requirements (was previously step 3)
      case 2:
        return (
          <div className="form-step">
            <BusinessLoanStep {...stepProps} />
            <div className="form-buttons">
              <PreviousButton />
              <NextButton />
            </div>
          </div>
        );
        
      // Step 3 is now Additional Info (was previously step 4)
      case 3:
        return (
          <div className="form-step">
            <BusinessAdditionalStep {...stepProps} />
            <div className="form-buttons">
              <PreviousButton />
              <SubmitButton />
            </div>
          </div>
        );
        
      default:
        return null;
    }
  }
  
  // If applicant type is not selected and trying to access steps 2-3, redirect to step 1
  if (currentStep > 1 && !formData.applicantType) {
    return (
      <div className="form-step">
        <div className="error-message-box">
          Please select an applicant type to proceed
        </div>
        <LoanTypeStep {...stepProps} />
        <div className="form-buttons">
          <button type="button" className="primary-button next-button" disabled={true}>
            <span className="button-text">Select an option above to continue</span>
          </button>
        </div>
      </div>
    );
  }
  
  return null;
};
